import React, { useState, useEffect, useRef } from 'react';
import {
  LTButton,
  LTCopyToClipboard,
  LTTag,
  LTText,
  LTTimeline,
  LTTooltip
} from '@lambdatestincprivate/lt-components';
import RCAIcons from './RCAIcons';
import StackTraceViewer from './StackTraceViewer';
import { getRootCauseIndex } from './helpers';
import { TagStyles } from './constants';
import SubTimelineConnector from '../../assets/SubTimelineConnector';
import styles from './AIRca.module.css';

const RCATimeline = ({ timelineData }) => {
  // Compute timeline data before state initialization
  const rootCauseIndex = getRootCauseIndex(
    Array.isArray(timelineData) ? timelineData : []
  );
  const errorsBeforeRootCause = Array.isArray(timelineData)
    ? timelineData.slice(0, rootCauseIndex + 1)
    : [];
  const errorsAfterRootCause = Array.isArray(timelineData)
    ? timelineData.slice(rootCauseIndex + 1)
    : [];

  // Auto-expand Root Cause and Last Effect by default (Req-2: TE-7518)
  // Initialize state directly from timelineData so they are expanded on
  // the very first render — no flash of collapsed content.
  const [selectedStep, setSelectedStep] = useState(
    rootCauseIndex >= 0 ? rootCauseIndex : null
  );
  const [selectedSubStep, setSelectedSubStep] = useState(
    rootCauseIndex >= 0 && errorsAfterRootCause.length > 0
      ? errorsAfterRootCause.length - 1
      : null
  );

  // Re-expand when timeline data changes (e.g. different test selected
  // while the drawer stays mounted).
  const prevTimelineRef = useRef(timelineData);
  useEffect(() => {
    if (prevTimelineRef.current === timelineData) return;
    prevTimelineRef.current = timelineData;
    if (rootCauseIndex >= 0 && Array.isArray(timelineData) && timelineData.length > 0) {
      setSelectedStep(rootCauseIndex);
      setSelectedSubStep(
        errorsAfterRootCause.length > 0 ? errorsAfterRootCause.length - 1 : null
      );
    } else {
      setSelectedStep(null);
      setSelectedSubStep(null);
    }
  }, [timelineData, rootCauseIndex, errorsAfterRootCause.length]);

  if (!Array.isArray(timelineData) || timelineData.length === 0) return null;

  return (
    <div className={styles.timelineContainer}>
      <div className={styles.timelineHeader}>
        <LTText variant={LTText.TextVariant.SUBHEADER_BOLD} color="text.muted">
          Event Timeline
        </LTText>
      </div>
      <div className={styles.timelineWrapper}>
        <LTTimeline>
          {errorsBeforeRootCause.map((step, index) => {
            const isExpanded = index === selectedStep;
            const timelineTagText =
              rootCauseIndex === index ? 'Root Cause' : 'Effect';
            const iconName = index > rootCauseIndex ? 'W' : step?.level;

            return (
              <LTTimeline.Item
                key={index}
                badgeIcon={<RCAIcons entity={iconName} />}
              >
                <LTTimeline.Body>
                  <div className={styles.stepContent}>
                    <div className={styles.stepHeader}>
                      <div className={styles.stepHeaderMain}>
                        <LTTooltip text={step.step_name}>
                          <LTText
                            variant={LTText.TextVariant.SUBHEADER_BOLD}
                            className={styles.stepTitle}
                          >
                            {step.step_name}
                          </LTText>
                        </LTTooltip>
                        <div>
                          {timelineTagText && (
                            <LTTag
                              customStyle={TagStyles[timelineTagText]}
                              text={timelineTagText}
                            />
                          )}
                        </div>
                      </div>
                      {step.stack_trace && (
                        <LTButton
                          variant={LTButton.Variant.INVISIBLE}
                          size={LTButton.Size.SMALL}
                          className={styles.stepToggle}
                          onClick={() =>
                            setSelectedStep(isExpanded ? null : index)
                          }
                        >
                          <LTText
                            variant={LTText.TextVariant.SUBHEADER_REGULAR}
                            className={styles.stepToggleText}
                          >
                            {isExpanded ? 'Hide' : 'Show'}
                          </LTText>
                        </LTButton>
                      )}
                    </div>
                    <LTText
                      variant={LTText.TextVariant.SMALL_REGULAR}
                      color="text.muted"
                    >
                      {step.summary}
                    </LTText>
                    {isExpanded && step.stack_trace && (
                      <div className={styles.stackTraceContainer}>
                        <div className={styles.stackTraceHeader}>
                          <LTText variant={LTText.TextVariant.SUBHEADER_BOLD}>
                            {step.source_log}
                          </LTText>
                          <div className={styles.copyActions}>
                            <LTCopyToClipboard textToCopy={step.stack_trace} />
                          </div>
                        </div>
                        <div className={styles.stackTraceBody}>
                          <StackTraceViewer value={step.stack_trace} />
                        </div>
                      </div>
                    )}
                  </div>

                  {index === rootCauseIndex && errorsAfterRootCause.length > 0 && (
                    <div>
                      <div>
                        {errorsAfterRootCause.map((subStep, subIndex) => {
                          const isSubExpanded = subIndex === selectedSubStep;
                          const subIconName =
                            subIndex > rootCauseIndex ? 'W' : subStep?.level;

                          return (
                            <div
                              key={subIndex}
                              className={styles.subTimelineRow}
                            >
                              <div className={styles.subTimelineConnector}>
                                <SubTimelineConnector />
                              </div>
                              <div className={styles.subStepContent}>
                                <div className={styles.stepHeader}>
                                  <div className={styles.stepHeaderMain}>
                                    <RCAIcons entity={subIconName} />
                                    <div className={styles.subStepInner}>
                                      <div className={styles.subStepNameRow}>
                                        <div className={styles.subStepNameTags}>
                                          <LTTooltip text={subStep.step_name}>
                                            <LTText
                                              variant={
                                                LTText.TextVariant
                                                  .SUBHEADER_BOLD
                                              }
                                              className={styles.stepTitle}
                                            >
                                              {subStep.step_name}
                                            </LTText>
                                          </LTTooltip>
                                          <LTTag
                                            customStyle={TagStyles['Effect']}
                                            text="Effect"
                                          />
                                        </div>
                                        {subStep.stack_trace && (
                                          <LTButton
                                            variant={LTButton.Variant.INVISIBLE}
                                            size={LTButton.Size.SMALL}
                                            className={styles.stepToggle}
                                            onClick={() =>
                                              setSelectedSubStep(
                                                isSubExpanded ? null : subIndex
                                              )
                                            }
                                          >
                                            <LTText
                                              variant={
                                                LTText.TextVariant
                                                  .SUBHEADER_REGULAR
                                              }
                                              className={styles.stepToggleText}
                                            >
                                              {isSubExpanded ? 'Hide' : 'Show'}
                                            </LTText>
                                          </LTButton>
                                        )}
                                      </div>
                                      <LTText
                                        variant={
                                          LTText.TextVariant.SMALL_REGULAR
                                        }
                                        color="text.muted"
                                      >
                                        {subStep.summary}
                                      </LTText>
                                    </div>
                                  </div>
                                </div>
                                {isSubExpanded && subStep.stack_trace && (
                                  <div className={styles.stackTraceContainer}>
                                    <div className={styles.stackTraceHeader}>
                                      <LTText
                                        variant={
                                          LTText.TextVariant.SUBHEADER_BOLD
                                        }
                                      >
                                        {subStep.source_log}
                                      </LTText>
                                      <div className={styles.copyActions}>
                                        <LTCopyToClipboard
                                          textToCopy={subStep.stack_trace}
                                        />
                                      </div>
                                    </div>
                                    <div className={styles.stackTraceBody}>
                                      <StackTraceViewer
                                        value={subStep.stack_trace}
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </LTTimeline.Body>
              </LTTimeline.Item>
            );
          })}

          {errorsBeforeRootCause.length > 0 && (
            <LTTimeline.Item
              key="session-finished"
              badgeIcon={<span className={styles.sessionDot} />}
            >
              <div className={styles.sessionContent}>
                <div className={styles.sessionHeader}>
                  <div className={styles.sessionHeaderMain}>
                    <LTTooltip text="Session Finished">
                      <LTText
                        text="Session Finished"
                        style={{ marginTop: '6px' }}
                        variant={LTText.TextVariant.SUBHEADER_REGULAR}
                      />
                    </LTTooltip>
                  </div>
                </div>
              </div>
            </LTTimeline.Item>
          )}
        </LTTimeline>
      </div>
    </div>
  );
};

export default RCATimeline;
